import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import TextWithIcon from '../../TextWithIcon';
import Link from '../../../Core/Link';
import RichText from '../../../Core/RichText';
import AppStores from '../../AppStores';

import {
  Card,
  Header,
  Body,
  Footer,
  ProductInfo,
  ImageWrapper,
  Img,
  Content,
  Heading,
} from './styles';

const header = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Heading>{children}</Heading>,
  },
};

const body = {
  renderNode: {
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul className="no-bullets">{children}</ul>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      return (
        <Link className="rich-text-link" to={data.uri} fontSize="inherit">
          {children}
        </Link>
      );
    },
  },
};

const link = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
  },
};

const bottomOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="tiny">{children}</p>,
  },
};

const Feature = ({ as = null, index = 0, className = '', data = null }) => {
  if (!data) return false;

  const {
    id,
    component,
    subheading,
    imageDam,
    iconToggle,
    heading,
    bodyCopy,
    ctas,
    badges,
    bottomCopy,
    textAlignment,
    imageToggle,
    cardClassName,
  } = data;

  // Get width of image if image exists
  const imgWidth =
    imageDam && imageDam.asset.localImage?.childImageSharp
      ? imageDam.asset.localImage?.childImageSharp?.gatsbyImageData
          .presentationWidth
      : 120;

  const [newImageWidth, setNewImgWidth] = useState(imgWidth);

  // If image is smaller than 640px use its actual size, otherwise set to a maxWidth of 640px
  useEffect(() => {
    if (imgWidth >= 640) {
      setNewImgWidth(640);
    }
  }, [newImageWidth]);

  return (
    <Card
      key={id}
      className={`${className} ${cardClassName} ${component} ${textAlignment}`}
      as={as}
    >
      {(subheading || heading || imageDam) && (
        <Header>
          {subheading && <p>{`${subheading} ${index + 1}`}</p>}
          {imageDam &&
            (iconToggle || imageToggle ? (
              <ImageWrapper>
                <Img
                  src={imageDam}
                  alt=""
                  role="presentation"
                  style={{
                    width: newImageWidth,
                  }}
                />
                {heading && <RichText document={heading} options={header} />}
              </ImageWrapper>
            ) : (
              <TextWithIcon
                type={`card ${textAlignment}`}
                icon={imageDam ? imageDam.asset.localImage.publicURL : null}
              >
                {heading && <RichText document={heading} options={header} />}
              </TextWithIcon>
            ))}
          {heading && !imageDam && (
            <RichText document={heading} options={header} />
          )}
        </Header>
      )}
      {(bodyCopy || badges) && (
        <Content>
          <Body>
            {heading && iconToggle && (
              <RichText document={heading} options={header} />
            )}
            {bodyCopy && <RichText document={bodyCopy} options={body} />}
            {badges && <AppStores badges={badges} direction="column" />}
          </Body>
        </Content>
      )}
      {(ctas || bottomCopy) && (
        <Footer>
          {ctas &&
            ctas.map(cta => {
              if (cta.hideFrench === true) return null;
              return (
                <Link key={cta.id} data={cta}>
                  <RichText document={cta.label} options={link} />
                </Link>
              );
            })}
          {bottomCopy && (
            <ProductInfo>
              {bottomCopy && (
                <RichText document={bottomCopy} options={bottomOptions} />
              )}
            </ProductInfo>
          )}
        </Footer>
      )}
    </Card>
  );
};

Feature.propTypes = {
  as: PropTypes.string,
  className: PropTypes.string,
  data: PropTypes.shape(),
  index: PropTypes.number,
};

export default Feature;
